import Layer from 'ol/layer/Tile';
import Source from 'ol/source/TileImage';
import TileGrid, { Options } from 'ol/tilegrid/TileGrid';
import { ImageTile, Tile, TileCache } from 'ol';
import { getRenderPixel } from 'ol/render';
import TileState from 'ol/TileState';
import { TileCoord } from 'ol/tilecoord';
import { Size, toSize } from 'ol/size';
import { Extent } from 'ol/extent';
import { getUid } from 'ol/util';

import { LineType } from '../enums/LineType';
import { IGeopostSeismicLayerParams } from '../interfaces/IGeopostSeismicLayerParams';
import { getParamsFromUrl } from 'features/seismic/utils/seismicUrlUtils';
import { GeopostMap } from './GeopostMap';
import { TileSourceStrategy } from './TileSourceStrategies/TileSourceStrategy';
import { getTrace } from 'features/seismic/utils/calculateScreenValuesUtils';
import { EXTERNAL_ACCESS_TOKEN_HEADER } from 'configurations/constants';

type TileErrorGeometry = {
    startTrace: number;
    endTrace: number;
    surveyLineIncrement: number;
    surveyLineStart: number;
    surveyLineEnd: number;
    scaleX: number;
    scaleY: number;
    startAmplitude: number;
    endAmplitude: number;
    sampleInterval: number;
    zoom: number;
    mapExtent: Extent | undefined;
};

export class GeopostSeismicLayer extends Layer<Source> {
    protected Source?: Source;
    Self?: GeopostSeismicLayer;

    protected _cacheTileSystem:{[url: string]:string} = {};
    protected _httpRequest:{
        ajax: XMLHttpRequest,
        tile: ImageTile
    }[] = [];

    protected objectZoomTestControler:{[zoom: number]: {
        lineStart: string,
        lineEnd: string,
        lineStartSample: string,
        lineEndSample: string,
        url: string
    }[]} = {};

    protected objectIgnoredTestControler:{[zoom: number]: {
        lineStart: string,
        lineEnd: string,
        lineStartSample: string,
        lineEndSample: string,
        url: string
    }[]} = {};

    protected count = 0;

    protected Params: IGeopostSeismicLayerParams = {
        accessToken: '',
        tenantToken: '',
        //gain: 0,
        absoluteGain: {
            min: -100,
            max: 100,
        },
        debug: false,
        resolutions: [],
        tileDimensionsPerResolution: [],
        extent: [0, 0, 0, 0],
        tileWidth: 0,
        tileHeight: 0,
        volumeToken: '',
        urlRender: '',
        filePath: '',
        colorbar: '',
        skipTraceCount: 1,
        lineNumber: 1,
        byteSize: 1,
        parallelProcesses: 1,
        dpi: 1,
        minSampleValue: 1,
        maxSampleValue: 1,
        jpgQuality: 1,
        isIndexedVolume: false,
        scaleX: 0,
        scaleY: 0,
        traceRangeStart: 0,
        traceRangeEnd: 0,
        isSwiped: false,
        swipeValue: 50,
        geopostMap: undefined,
        sampleInterval: 0,
        applyWatermark: false,
        inlineDimensionName: 'inline',
        xlineDimensionName: 'xline',
        sampleDimensionName: 'sample',
        loadingLayer: undefined,
        loadImageDebounce: 25,
        renderLimits: [],
        volumeSamplesPerTrace: 0,
        numberOfTraces: 0,
        verticallyInverted: false,
        lineType: LineType.Inline,
        prepareRenderChunkSize: 128,
        attributeCalculationMethod: 0,
        opacity: 1,
        useTraceRange: false,
        baseInlineStart: 0,
        baseInlineEnd: 0,
        baseXlineStart: 0,
        baseXlineEnd: 0,
    };
    protected minSampleValue = 0;
    protected maxSampleValue = 0;
    protected timeDebugging: boolean;
    protected prepareLoadingKeys: string[] = [];
    public tileSourceStrategy: TileSourceStrategy;
    map?: GeopostMap;

    private durationLastTimeOutCheck = 20000;
    private lastTimeOutRenderCheck?: NodeJS.Timeout;
    private invertedInLastLoad: boolean = false;

    //dictionariesRetries:{[key:string]:number} = {};

    tileGrid: TileGrid | null = null;

    constructor(params: IGeopostSeismicLayerParams, tileSourceStrategy: TileSourceStrategy, timeDebugging: boolean = false) {
        super({
            preload: 0,
            visible: true,
            useInterimTilesOnError: false,
        });
        this.tileSourceStrategy = tileSourceStrategy;
        tileSourceStrategy.setRefMinSampleValue(params.absoluteGain?.min ?? 0);
        tileSourceStrategy.setRefMaxSampleValue(params.absoluteGain?.max ?? 0);
        this.updateParams(params);

        this.buildSource();
        this.setSwipeEvents();
        this.timeDebugging = timeDebugging;

        console.log('iniciando novo timeout de check');

        this.lastTimeOutRenderCheck = setInterval(() => {
            console.log('exectando timeoutde check');
            //const array = this._httpRequest.map(x=>x.ajax.status).filter(x=>x !== 200 && x !== 401 && x !== 0);
            if (this._httpRequest.length === 0){
                return;
            }
            const array = this._httpRequest.map(x=>x.ajax.readyState).filter(x=>x !== 4);
            if (array.length === 0){
                this._httpRequest = [];
                //this.clearLoadingBarriers();
                console.log('cleaning loadings');
                clearInterval(this.lastTimeOutRenderCheck);
            }
        }, this.durationLastTimeOutCheck);

        /*setTimeout(() => {
            console.log('trocando');
            this.Params.accessToken = 'adas';
        }, 10000);*/

        /*setInterval(() => {
            //const array = this._httpRequest.map(x=>x.ajax.status).filter(x=>x !== 200 && x !== 401 && x !== 0);
            if (this._httpRequest.length === 0){
                return;
            }
            const array = this._httpRequest.map(x=>x.ajax.readyState).filter(x=>x !== 4);

            if (array.length === 0){
                console.log(this._httpRequest.map(x=>x.ajax.readyState));
                this._httpRequest = [];
                this.clearLoadingBarriers();
                console.log('limpand loadings');
            }
        }, 20000); */
    }

    rebuildSource() {
        this.buildSource();
    }

    protected buildGrid() {
        console.log(this.Params.resolutions);
        console.log(this.Params.tileDimensionsPerResolution);

        const tileGridParams : Options = {
            extent: this.Params.extent!,
            minZoom: 0,
            resolutions: this.Params.resolutions!,
        };

        if (!!this.Params.tileDimensionsPerResolution) {
            tileGridParams.tileSizes = this.Params.tileDimensionsPerResolution;
        }
        else {
            tileGridParams.tileSize = [this.Params.tileWidth!, this.Params.tileHeight!];
        }

        return new TileGrid(tileGridParams);
    }

    protected buildSource() {
        const tileGrid = this.buildGrid();
        const source = new Source({
            cacheSize: 0,
            interpolate: true,
            tileGrid: tileGrid,
            tilePixelRatio: 1,
            transition: 200,
            zDirection: 0
        });

        this.tileGrid = tileGrid;

        this.setSource(source);
        this.Source = source;

        this.setTileUrlFunction();
        this.setTileLoadFunction();
    }

    protected finishTileLoad(fullUrl: string) {
        const key = this.getTileLoadingKey(fullUrl);
        if (this.Params.loadingLayer){
            this.Params.loadingLayer?.hideLoading(key);
        }

        this.Params.increaseLoaded!(fullUrl);
    }

    protected endTileTransition(tile: ImageTile) {
        const renderer = this.getRenderer();
        if (renderer) {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            tile.endTransition(getUid(renderer as any));
        }
    }

    private static readonly BLANK_TILE_IMAGE = 'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAQAAAC1HAwCAAAAC0lEQVR42mNkYAAAAAYAAjCB0C8AAAAASUVORK5CYII=';
    private static readonly ERROR_RETRY_AFTER_MS = 5000;

    protected markTileAsFailed(tile: ImageTile) {
        this.setImageOnTile(tile, GeopostSeismicLayer.BLANK_TILE_IMAGE);
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (tile as any)._geopostErrorSentinel = true;
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (tile as any)._geopostErrorTimestamp = Date.now();
        this.endTileTransition(tile);
    }

    protected markTileAsDiscarded(tile: ImageTile) {
        (tile.getImage() as HTMLImageElement).src = GeopostSeismicLayer.BLANK_TILE_IMAGE;
        tile.setState(TileState.ERROR);
        this.endTileTransition(tile);
    }

    setSwipeEvents() {
        this.on('prerender', (event) => {
            const ctx = event.context as CanvasRenderingContext2D;
            if (this.Params.geopostMap && this.Params.swipeValue) {
                const mapSize = this.Params.geopostMap.getSize();
                if (mapSize) {
                    const width = mapSize[0] * (this.Params.swipeValue / 100);
                    const tl = getRenderPixel(event, [width, 0]);
                    const tr = getRenderPixel(event, [mapSize[0], 0]);
                    const bl = getRenderPixel(event, [width, mapSize[1]]);
                    const br = getRenderPixel(event, mapSize);
                    if (ctx) {
                        ctx.save();
                        if (this.Params.isSwiped) {
                            ctx.beginPath();
                            ctx.moveTo(tl[0], tl[1]);
                            ctx.lineTo(bl[0], bl[1]);
                            ctx.lineTo(br[0], br[1]);
                            ctx.lineTo(tr[0], tr[1]);
                            ctx.closePath();
                            ctx.clip();
                        }
                    }
                }
            }
        });

        this.on('postrender', function (event) {
            const ctx = event.context as CanvasRenderingContext2D;
            ctx.restore();
        });
    }

    stopRequests(ignoreByZoom = -1){
        if (this.Params.resetLoadingControl){
            this.Params.resetLoadingControl();
        }

        this._httpRequest.forEach(item => {
            item.tile.setState(TileState.LOADED); //LOADED
            item.ajax.abort();
        });

        if (this.Params.loadingLayer){
            this.Params.loadingLayer.clearAllLoading();
        }
        this._httpRequest = [];
    }

    updateParams(params: IGeopostSeismicLayerParams, resetPreviousRender: boolean = true) {
        //console.log('update params');
        this.Params = Object.assign(this.Params, params);

        this.updateGain();
        if (resetPreviousRender) {
            //this.stopRequests();
        }

        return this;
    }

    updateGain() {
        this.minSampleValue = this.Params.absoluteGain?.min ?? 0;
        this.maxSampleValue = this.Params.absoluteGain?.max ?? 0;
    }

    protected setTileUrlFunction() {
        if (!this.Source) {
            return;
        }
        this.Source.setTileUrlFunction(this.tileUrlFunction);

    }

    public clearLoadingBarriers = () => {
        this.Params.loadingLayer?.clearAllLoading();
        this.Params.resetLoadingControl!();
    };

    protected tileUrlFunction = () => {
        return this.tileSourceStrategy.getTileUrl(this.Params);
    };

    private addParamToUrl(param: string) {
        return `&${param}=[${param}]`;
    }

    private addParamValueToUrl(param: string, value: string) {
        return `&${param}=${value}`;
    }

    getTileCoordKey(tileCoord: TileCoord){
        return `${tileCoord[0]}-${tileCoord[1]}-${tileCoord[2]}`;
    }

    reload(range: [number, number] | null = null) {
        //reload é pra evitar o blink
        setTimeout(() => {
            const view = this.map!.getView()!;
            const zoom = view.getZoom()!;
            const extent = view.calculateExtent(this.map!.getSize());

            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const objectTileGridToIgnore: {[key:string]: boolean} = {};
            const source = this.getSource()!;

            let tiles: ImageTile[] = [];
            if (this.Params.lineType !== LineType.ZSlice) {
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                tiles = (this.getRenderer() as any).renderedTiles as ImageTile[];
            }
            else {
                source.getTileGrid()!.forEachTileCoord(extent, zoom, (tileCoordinate) => {
                    const tile = source.getTile(tileCoordinate[0], tileCoordinate[1], tileCoordinate[2], window.devicePixelRatio, view.getProjection()) as ImageTile;
                    tiles.push(tile);
                });
            }

            console.log('tiles ------>', tiles);

            this.getSource()!.getTileGrid()!.forEachTileCoord(extent, zoom, (a) => {
                objectTileGridToIgnore[this.getTileCoordKey(a)] = true;
            });

            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const tileCache = (this.getSource() as any).tileCache as TileCache;
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const entries = (tileCache as any).entries_ as {[value_:string]:EntryCache};

            for (let e in entries){
                const entry = entries[e];
                const tile = entry.value_;
                const key = this.getTileCoordKey(tile.getTileCoord());

                if (objectTileGridToIgnore[key]){
                }
                else {
                    tileCache.remove(e);
                }
            }

            //tileCache.ent
            //tileCache.clear();
            //linha 290 - getTile
            //getTileInternal
            //getTileCacheForProjection
            for (let tile of tiles) {

                const url = this.tileUrlFunction();
                this.tileLoadFunction(tile, url, range);
            }
        }, 50);
    }

    evictStaleErrorTiles(){
        const source = this.getSource();
        const view = this.map?.getView();
        if (!source || !view || !this.tileGrid){
            return;
        }

        const zoom = view.getConstrainedZoom(view.getZoom(), 0) ?? 0;
        const extent = view.calculateExtent(this.map!.getSize());

        const usedTileKeys: {[key: string]: boolean} = {};
        this.tileGrid.forEachTileCoord(extent, zoom, (tileCoordinate) => {
            usedTileKeys[this.getTileCoordKey(tileCoordinate)] = true;
        });

        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const tileCache = (source as any).tileCache as TileCache;
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const entries = (tileCache as any).entries_ as {[key: string]: EntryCache};

        let evictedAny = false;

        for (let entryKey in entries){
            const entry = entries[entryKey];
            const tile = entry.value_;
            const tileCoordKey = this.getTileCoordKey(tile.getTileCoord());

            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const isFailedTile = (tile as any)._geopostErrorSentinel === true || tile.getState() === TileState.ERROR;
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const failedAt = (tile as any)._geopostErrorTimestamp as number | undefined;
            const isStaleEnoughToRetry = !failedAt || (Date.now() - failedAt) > GeopostSeismicLayer.ERROR_RETRY_AFTER_MS;

            if (isFailedTile && (!usedTileKeys[tileCoordKey] || isStaleEnoughToRetry)){
                tileCache.remove(entryKey);
                tile.release();
                evictedAny = true;
            }
        }

        if (evictedAny){
            this.getSource()?.changed();
        }
    }

    protected setTileLoadFunction() {
        if (!this.Source) {
            return;
        }

        this.Source.setTileLoadFunction(this.tileLoadFunction);
    }

    protected getTileLoadingKey = (fullUrl: string) => {
        return this.tileSourceStrategy.getTileLoadingKey(fullUrl);
    };

    protected extractUrlValue = (url: string, key: string) =>
    {
        const match = url.match('[?&]' + key + '=([^&]+)');
        return match ? match[1] : null;
    };

    protected getTileSize() {
        return toSize((this.tileGrid?.getTileSize(this.map?.getView().getConstrainedZoom(this.map?.getView().getZoom(), 0) ?? 0) ?? [ this.Params.tileWidth!, this.Params.tileHeight!]));
    }

    protected getFullUrl = (src: string, xStart: number, xEnd: number, yStart: number, yEnd: number, minGain: number, maxGain: number, surveyLineStart: number, surveyLineEnd: number) => {
        const tileSize = this.getTileSize();
        return this.tileSourceStrategy.getFullUrl(src, xStart, xEnd, yStart, yEnd, minGain, maxGain, surveyLineStart, surveyLineEnd).replace('[tileWidth]', tileSize[0].toString()).replace('[tileHeight]', tileSize[1].toString());
    };

    protected tileLoadFunction = (tile: Tile, src: string, range: [number, number] | null = null) => {
        let [ surveyLineStart, surveyLineEnd, surveyLineIncrement, baseLineStart, baseLineEnd, baseLineIncrement ] = this.tileSourceStrategy.getSurveyLineDimensions(this.Params);

        const tileSize = this.getTileSize();
        const axisXTraceFactor = tileSize[0]! * this.Params.resolutions![tile.tileCoord[0]] / this.Params.scaleX! * baseLineIncrement;
        const startOffset = ((this.Params.isInverted ? (surveyLineEnd - baseLineEnd) * -1 : (surveyLineStart - baseLineStart)) * -1);
        surveyLineStart = surveyLineStart + startOffset;

        const startTrace = Math.round(getTrace(Math.max(this.Params.extent![0], surveyLineStart), this.Params.scaleX!, surveyLineStart, baseLineIncrement) + (tile.tileCoord[1] * axisXTraceFactor));
        let endTrace = Math.round(getTrace(Math.max(this.Params.extent![0], surveyLineStart), this.Params.scaleX!, surveyLineStart, baseLineIncrement) + ((tile.tileCoord[1] + 1) * axisXTraceFactor));

        if (!!range) {
            if (endTrace < range[0] || startTrace > range[1]) {
                return;
            }
        }

        const startSampleOffset = this.Params.lineType === LineType.ZSlice ? (this.Params.verticallyInverted ? (this.Params.surveyXlineEnd! - this.Params.baseXlineEnd!) * -1 : (this.Params.surveyXlineStart! - this.Params.baseXlineStart!)) / (this.Params.baseXlineIncrement || 1) * -1: 0;

        const pixelPerSample = parseInt((tileSize[1]! * this.Params.resolutions![tile.tileCoord[0]] / this.Params.scaleY!).toString());
        const startAmplitude = (this.Params.extent![3] - tile.tileCoord[2] * pixelPerSample) * -1 + startSampleOffset;
        const endAmplitude = (this.Params.extent![3] - ((tile.tileCoord[2] + 1) * pixelPerSample)) * -1 + startSampleOffset;

        const sampleIntervalRatio = (this.Params.lineType !== LineType.ZSlice && !!this.Params.baseSampleInterval && !!this.Params.sampleInterval)
            ? this.Params.baseSampleInterval / this.Params.sampleInterval
            : 1;
        const nativeStartAmplitude = Math.round(startAmplitude * sampleIntervalRatio);
        const nativeEndAmplitude = Math.round(endAmplitude * sampleIntervalRatio);

        let  minSample:number = this.minSampleValue;
        let  maxSample:number = this.maxSampleValue;

        minSample = this.Params.absoluteGain?.min ?? -100;
        maxSample = this.Params.absoluteGain?.max ?? 100;

        const mapExtent = this.map?.getView().calculateExtent(this.map.getSize());

        const fullUrl = this.getFullUrl(src, startTrace, endTrace, nativeStartAmplitude, nativeEndAmplitude, minSample, maxSample, this.Params.surveyLineStart ?? surveyLineStart, this.Params.surveyLineEnd ?? surveyLineEnd);

        const tileLoadingKey = this.getTileLoadingKey(fullUrl);

        const totalTraces = (surveyLineEnd - surveyLineStart) / surveyLineIncrement;
        const prepareRenderStep = getParamsFromUrl().prepareRenderStep;
        const totalPrepareChunks = Math.ceil(totalTraces / (prepareRenderStep * this.Params.prepareRenderChunkSize!));

        const limits = this.Params.renderLimits!;

        //verifica se o prepareRender ainda não preparou todos os tiles
        /*console.log(`comparison ${limits.length} x ${totalPrepareChunks}`);
        if (limits.length > totalPrepareChunks) {
            console.log('a');
        }*/

        if (this.Params.lineType === LineType.ZSlice && tile.getState() === TileState.EMPTY) {
            tile.load();
            this.Params.increaseLoading!(fullUrl);
            return;
        }

        if (limits.length < totalPrepareChunks) {
            //verifica interseção com os limites de renderização definidos
            let intersectionRange = 0;
            for (let i = 0; i < limits.length; i++) {
                const limit = limits[i];
                let lowerLimit = limit[0];
                let upperLimit = limit[1];
                if (startTrace > upperLimit || lowerLimit > endTrace) {
                    continue;
                }
                intersectionRange += Math.min(upperLimit, endTrace) - Math.max(lowerLimit, startTrace);
            }

            if (intersectionRange < (Math.min(endTrace, surveyLineEnd) - Math.max(startTrace, surveyLineStart))) {
                tile.setState(TileState.LOADED);
                this.Params.increaseLoading!(fullUrl);
                return;
            }
        }

        const restrictGeometry = this.Params.restrictedAreaGeometry;
        const tileExtent = this.Params.isInverted ?
            [surveyLineEnd - endTrace + surveyLineStart, startAmplitude, surveyLineEnd - startTrace + surveyLineStart, endAmplitude]
            :
            [startTrace, startAmplitude, endTrace, endAmplitude];
        if (!!restrictGeometry && !restrictGeometry.intersectsExtent(tileExtent)) {
            (tile as ImageTile).setImage(new Image());
            //((tile as ImageTile).getImage() as HTMLImageElement).style.visibility = 'hidden !important';
            //((tile as ImageTile).getImage() as HTMLImageElement).width = 0;
            //((tile as ImageTile).getImage() as HTMLImageElement).height = 0;
            tile.setState(TileState.LOADED);
            this.Params.increaseLoading!(fullUrl);
            //this.getSource()?.changed();
            setTimeout(() => this.getSource()?.changed(), 50);
            return;
        }

        const volumeSamplesPerTrace = this.Params.volumeSamplesPerTrace;
        if (this.Params.lineType !== LineType.ZSlice && !!volumeSamplesPerTrace && nativeStartAmplitude >= volumeSamplesPerTrace) {
            (tile as ImageTile).setImage(new Image());
            tile.setState(TileState.LOADED);
            this.Params.increaseLoading!(fullUrl);
            setTimeout(() => this.getSource()?.changed(), 50);
            return;
        }

        const zoom = this.map?.getView().getConstrainedZoom(this.map?.getView().getZoom(), 0) ?? 0;

        const cachedTileEntry = this._cacheTileSystem[this.getCacheEntry(fullUrl)];

        const hasCache = (!!cachedTileEntry);

        /*if (this.lastTimeOutRenderCheck){
            clearInterval(this.lastTimeOutRenderCheck);
        }*/

        setTimeout(() => {
            const currentZoom = this.map?.getView().getConstrainedZoom(this.map?.getView().getZoom(), 0) ?? 0;
            if (currentZoom === zoom && true){
                //console.log('Carregando tile');

                const currentResolution = this.map?.getView().getResolution();
                const loadingCircleRadius = (!!currentResolution && currentResolution > 5) ? 5 : 15;

                this.Params.loadingLayer?.showLoading(tileLoadingKey, startTrace, endTrace, baseLineIncrement, baseLineStart, baseLineEnd, this.Params.scaleX!, this.Params.scaleY!, startAmplitude * -1, endAmplitude * -1, this.Params.sampleInterval!,  currentZoom,  mapExtent, undefined, loadingCircleRadius);

                const tileErrorGeometry: TileErrorGeometry = {
                    startTrace,
                    endTrace,
                    surveyLineIncrement: baseLineIncrement,
                    surveyLineStart: baseLineStart,
                    surveyLineEnd: baseLineEnd,
                    scaleX: this.Params.scaleX!,
                    scaleY: this.Params.scaleY!,
                    startAmplitude: startAmplitude * -1,
                    endAmplitude: endAmplitude * -1,
                    sampleInterval: this.Params.sampleInterval!,
                    zoom: currentZoom,
                    mapExtent
                };

                this.loadImage(fullUrl, tile as ImageTile, 0, tileErrorGeometry);
            }
            else {
                this.markTileAsDiscarded(tile as ImageTile);
                //console.log('Descartando tile');
            }
        }, hasCache ? 5 : this.Params.loadImageDebounce);
    };

    protected setImageOnTile(tile: ImageTile, urlData: string){
        if (!!this.Params.restrictedAreaGeometry) {
            const image = new Image();
            image.src = urlData;
            tile.setImage(image);
        }
        else {
            (tile.getImage() as HTMLImageElement).src = urlData;
        }

        tile.setState(TileState.LOADED);

        setTimeout(() => {
            this.getSource()?.changed();
        }, 50);
    }

    protected getCacheEntry(fullUrl: string) {
        const currentZoom = Math.round(this.map?.getView().getZoom() ?? 0);
        //console.log('ZOOM -============>', currentZoom);
        return fullUrl + `--scaleY=${this.Params.scaleY}-scaleX=${this.Params.scaleX}--zoom=${currentZoom}--window=${window.innerWidth}x${window.innerHeight}`;
    }

    protected loadImage(fullUrl: string, tile: ImageTile, retry: number, tileErrorGeometry?: TileErrorGeometry) {
        this.Params.increaseLoading!(fullUrl);
        const cacheEntry = this.getCacheEntry(fullUrl);
        const cachedTileEntry = this._cacheTileSystem[cacheEntry];

        /*if (!this.tileToError){
            this.tileToError = fullUrl;
        }*/

        if (!!cachedTileEntry){
            if (cachedTileEntry === 'loading') {
                return;
            }
            this.setImageOnTile(tile, this._cacheTileSystem[cacheEntry]);
            this.finishTileLoad(fullUrl);
            return;
        }

        this.count++;
        //console.log(this.count);

        this._cacheTileSystem[cacheEntry] = 'loading';
        const xhr = new XMLHttpRequest();

        this._httpRequest.push({
            ajax: xhr,
            tile: tile
        });

        //let paramsPost =  (this.tileToError === fullUrl) ? 'https://asasasasa.comansa':fullUrl.replace(this.Params.urlRender + '?', '');
        let paramsPost = fullUrl.replace(this.Params.urlRender + '?', '');
        xhr.responseType = 'blob';
        const requestSendAt = new Date();

        xhr.timeout = 360000;

        const handleSuccess = () => {
            const data = xhr.response;
            if (data !== null) {
                if (data !== undefined) {
                    const urlData = URL.createObjectURL(data);
                    this._cacheTileSystem[cacheEntry] = urlData;

                    this.setImageOnTile(tile, urlData);
                    this.finishTileLoad(fullUrl);
                    (tile.getImage() as HTMLImageElement).src = urlData;

                    this.tileSourceStrategy.onSettleTileRequest(xhr, this.Params.lineNumber!, this.Params.tileWidth!, requestSendAt);
                } else {
                    delete this._cacheTileSystem[cacheEntry];
                    this.markTileAsFailed(tile);
                    this.finishTileLoad(fullUrl);
                }

            } else {
                delete this._cacheTileSystem[cacheEntry];
                this.markTileAsFailed(tile);
                this.finishTileLoad(fullUrl);
            }

        };

        const reCall = () => {
            //console.log('retrying tile ' + tile.tileCoord + ': ' + retry + 1);
            delete this._cacheTileSystem[cacheEntry];
            this.loadImage(fullUrl, tile, retry + 1, tileErrorGeometry);
        };

        const handleError = () => {
            //console.log('error on load tile ' + tile.tileCoord + ': ' + retry);
            /*if (!this.dictionariesRetries[tile.tileCoord.toString()]){
                this.dictionariesRetries[tile.tileCoord.toString()] = 0;
            }
            this.dictionariesRetries[tile.tileCoord.toString()]++;
            */
            //console.log(this.dictionariesRetries);

            if (retry <= 2){
                reCall();
            } else {
                const tileSize = this.getTileSize();
                const lineIncrement = this.tileSourceStrategy.getSurveyLineDimensions(this.Params)[2];
                const axisXTraceFactor = tileSize[0]! * this.Params.resolutions![tile.tileCoord[0]] / this.Params.scaleX! * lineIncrement;
                const startTrace = this.Params.extent![0] + (tile.tileCoord[1] * axisXTraceFactor);
                let endTrace = this.Params.extent![0] + ((tile.tileCoord[1] + 1) * axisXTraceFactor);
                this.tileSourceStrategy.onTileRequestError(xhr, this.Params.lineNumber!, this.Params.tileWidth!, startTrace, endTrace);

                delete this._cacheTileSystem[cacheEntry];
                this.markTileAsFailed(tile);
                this.finishTileLoad(fullUrl);

                if (tileErrorGeometry){
                    const currentZoom = this.map?.getView().getConstrainedZoom(this.map?.getView().getZoom(), 0) ?? 0;
                    if (currentZoom === tileErrorGeometry.zoom){
                        this.Params.loadingLayer?.showError(
                            this.getTileLoadingKey(fullUrl),
                            tileErrorGeometry.startTrace,
                            tileErrorGeometry.endTrace,
                            tileErrorGeometry.surveyLineIncrement,
                            tileErrorGeometry.surveyLineStart,
                            tileErrorGeometry.surveyLineEnd,
                            tileErrorGeometry.scaleX,
                            tileErrorGeometry.scaleY,
                            tileErrorGeometry.startAmplitude,
                            tileErrorGeometry.endAmplitude,
                            tileErrorGeometry.sampleInterval,
                            tileErrorGeometry.zoom,
                            tileErrorGeometry.mapExtent
                        );
                    }
                }
            }
        };

        xhr.onreadystatechange = (ev) => {
            if (xhr.readyState === 4) {
                if (xhr.status === 0) {
                    handleError();
                }
                else if (xhr.status < 300) {
                    handleSuccess();
                } else {
                    handleError();
                }
            }
        };

        if (this.Params.debug) {
            xhr.open('POST', `${this.tileSourceStrategy.getEndpoint()}?z=${tile.tileCoord[0]}&x=${tile.tileCoord[1]}&y=${tile.tileCoord[2]}`);
        }
        else {
            xhr.open('POST',  this.tileSourceStrategy.getEndpoint());
        }
        xhr.setRequestHeader('Content-type', 'application/x-www-form-urlencoded');
        xhr.setRequestHeader('isGeopost', 'true');
        xhr.setRequestHeader('Authorization', 'Bearer ' + this.Params.accessToken);
        xhr.setRequestHeader(EXTERNAL_ACCESS_TOKEN_HEADER, 'Bearer ' + getParamsFromUrl().externalSSOAccessToken);
        xhr.setRequestHeader('x-gp-debug-active', this.timeDebugging.toString());

        // if (!myLocalCache[paramsPost])

        //paramsPost += '&zoom=' + zoom;

        const delayTileLoad = getParamsFromUrl().delayTileLoad;
        if (delayTileLoad > 0) {
            setTimeout(() => {
                xhr.send(paramsPost);
            }, delayTileLoad);
        } else {
            xhr.send(paramsPost);
        }
    }
    getParams(){
        return this.Params;
    }
    getSampleValues(){
        return {minSampleValue: this.minSampleValue , maxSampleValue: this.maxSampleValue};
    }
    setTileSourceStrategy(strategy: TileSourceStrategy){
        this.tileSourceStrategy = strategy;
        this.rebuildSource();
    }
}

interface EntryCache {
    value_: ImageTile;
}